// The pages below `/settings/accounts`: the list, the add form, one account (edit, calendars) and a calendar (sharing).
export type AccountsView =
  | { view: 'list' }
  | { view: 'new' }
  | { view: 'account' | 'edit' | 'calendars'; id: number }
  | { view: 'calendar' | 'sharing'; id: number; calendarId: number }
  | { view: 'unknown' }

const NUMBER = /^\d+$/

// `rest` is the route's `:rest*` parameter (what follows `/settings/accounts/`): `[]`, `['new']`, `['3']`, `['3', 'edit']`,
// `['3', 'calendars']`, `['3', 'calendars', '8']` or `['3', 'calendars', '8', 'sharing']`.
export function parseAccountsPath(rest: string | string[] | undefined): AccountsView {
  const parts = (Array.isArray(rest) ? rest : [rest ?? '']).filter((part) => part !== '')
  if (!parts.length) return { view: 'list' }
  if (parts.length === 1 && parts[0] === 'new') return { view: 'new' }
  if (!NUMBER.test(parts[0])) return { view: 'unknown' }
  const id = Number(parts[0])
  if (parts.length === 1) return { view: 'account', id }
  if (parts.length === 2) {
    return parts[1] === 'edit' || parts[1] === 'calendars' ? { view: parts[1], id } : { view: 'unknown' }
  }
  if (parts[1] !== 'calendars' || !NUMBER.test(parts[2])) return { view: 'unknown' }
  const calendarId = Number(parts[2])
  if (parts.length === 3) return { view: 'calendar', id, calendarId }
  return parts.length === 4 && parts[3] === 'sharing' ? { view: 'sharing', id, calendarId } : { view: 'unknown' }
}

export function accountsPath(
  id?: number,
  sub?: 'edit' | 'calendars',
  calendarId?: number,
  calendarSub?: 'sharing',
): string {
  return ['/settings/accounts', id, sub, calendarId, calendarSub].filter((part) => part !== undefined).join('/')
}

export const NEW_ACCOUNT_PATH = '/settings/accounts/new'

// Where Back goes from a page of the accounts: one level up, the list goes to `/settings`.
export function accountsParent(view: AccountsView): string {
  switch (view.view) {
    case 'list':
      return '/settings'
    case 'new':
    case 'unknown':
      return accountsPath()
    case 'account':
      return accountsPath()
    case 'edit':
    case 'calendars':
      return accountsPath(view.id)
    case 'calendar':
      return accountsPath(view.id, 'calendars')
    case 'sharing':
      return accountsPath(view.id, 'calendars', view.calendarId)
  }
}
